import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [success, setSuccess] = useState(false);
  
  // Colors from our app's theme
  const COLORS = {
    primary: '#243444',
    secondary: '#D2BA7B',
    tertiary: '#4C5454',
    background: '#FFFAFA',
    white: '#FFFFFF',
    red: '#7C1227'
  };
  
  // Styles
  const styles = {
    container: {
      maxWidth: '600px',
      margin: '40px auto',
      padding: '30px',
      backgroundColor: COLORS.background,
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      color: COLORS.primary
    },
    header: {
      backgroundColor: COLORS.primary,
      padding: '20px',
      marginBottom: '30px',
      borderRadius: '8px 8px 0 0',
      marginTop: '-30px',
      marginLeft: '-30px',
      marginRight: '-30px',
      textAlign: 'center'
    },
    title: {
      color: COLORS.secondary,
      margin: 0,
      fontSize: '28px'
    },
    subtitle: {
      color: COLORS.white,
      margin: '10px 0 0',
      fontSize: '16px',
      fontWeight: 'normal'
    },
    form: {
      marginTop: '20px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: 'bold',
      color: COLORS.primary
    },
    input: {
      width: '100%',
      padding: '12px',
      borderRadius: '4px',
      border: `1px solid ${COLORS.tertiary}`,
      fontSize: '16px',
      backgroundColor: COLORS.white
    },
    button: {
      backgroundColor: COLORS.secondary,
      color: COLORS.primary,
      border: 'none',
      padding: '12px 24px',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      width: '100%',
      marginTop: '10px'
    },
    error: {
      color: COLORS.red,
      marginTop: '20px',
      padding: '10px',
      backgroundColor: '#ffebee',
      borderRadius: '4px',
      textAlign: 'center'
    },
    success: {
      color: '#2e7d32',
      marginTop: '20px',
      padding: '10px',
      backgroundColor: '#e8f5e9',
      borderRadius: '4px',
      textAlign: 'center'
    },
    info: {
      backgroundColor: '#f5f5f5',
      padding: '15px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    link: {
      color: COLORS.primary,
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  };
  
  // Fetch invitation details when component mounts
  useEffect(() => {
    const verifyInvitation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/invitations/${token}`);
        setInvitation(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error verifying invitation:', err);
        setError(err.response?.data?.msg || 'Invalid or expired invitation');
        setLoading(false);
      }
    };
    
    if (token) {
      verifyInvitation();
    } else {
      setError('Invalid invitation link');
      setLoading(false);
    }
  }, [token]);
  
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name) {
      setError('Please enter your name');
      return;
    }
    
    if (!formData.password) {
      setError('Please enter a password');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Accept invitation
      await axios.post(`/api/invitations/${token}/accept`, {
        name: formData.name,
        password: formData.password
      });
      
      setSuccess(true);
      setLoading(false);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.msg || 'Error accepting invitation');
      setLoading(false);
    }
  };
  
  // Show loading state
  if (loading && !invitation) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Business Resilience Navigator</h1>
          <h2 style={styles.subtitle}>Verifying invitation...</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <p>Please wait while we verify your invitation...</p>
        </div>
      </div>
    );
  }
  
  // Show error if invitation is invalid
  if (error && !invitation) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Business Resilience Navigator</h1>
          <h2 style={styles.subtitle}>Invalid Invitation</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={styles.error}>{error}</div>
          <p style={{ marginTop: '20px' }}>
            Please contact the person who invited you for a new invitation link,
            or <span style={styles.link} onClick={() => navigate('/login')}>login here</span> if you already have an account.
          </p>
        </div>
      </div>
    );
  }
  
  // Show success message
  if (success) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Business Resilience Navigator</h1>
          <h2 style={styles.subtitle}>Invitation Accepted</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <div style={styles.success}>
            <p>Your account has been created successfully!</p>
            <p>You will be redirected to the login page in a few seconds...</p>
          </div>
        </div>
      </div>
    );
  }
  
  // Show invitation acceptance form
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Business Resilience Navigator</h1>
        <h2 style={styles.subtitle}>Accept Invitation</h2>
      </div>
      
      {invitation && (
        <div>
          <div style={styles.info}>
            <p><strong>You've been invited!</strong></p>
            <p>{invitation.invitedByName} has invited you to join <strong>{invitation.organizationName}</strong> on the Business Resilience Navigator platform.</p>
            <p>Please complete the form below to accept the invitation and create your account.</p>
          </div>
          
          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="text"
                style={{...styles.input, backgroundColor: '#f0f0f0'}}
                value={invitation.email}
                disabled
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Your Name</label>
              <input
                type="text"
                name="name"
                style={styles.input}
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                name="password"
                style={styles.input}
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password (min. 6 characters)"
                required
                minLength="6"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                style={styles.input}
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
              />
            </div>
            
            {error && <div style={styles.error}>{error}</div>}
            
            <button
              type="submit"
              style={styles.button}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Accept Invitation & Create Account'}
            </button>
          </form>
          
          <p style={{ marginTop: '20px', textAlign: 'center' }}>
            Already have an account? <span style={styles.link} onClick={() => navigate('/login')}>Login here</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default AcceptInvite;
